// MobileOfflineManager.js - Offline functionality for mobile app
// Handles data caching, offline queue, and sync when online

export default class MobileOfflineManager {
  constructor() {
    this.dbName = 'DeckOpsOfflineDB';
    this.dbVersion = 1;
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncQueue = [];
    this.offlineData = {
      userProfile: null,
      gameStats: {},
      philosophyFlowSessions: [],
      analytics: [],
      preferences: {}
    };
    
    this.init();
  }

  async init() {
    console.log('💾 MobileOfflineManager: Initializing offline support...');
    
    // Initialize IndexedDB
    await this.initIndexedDB();
    
    // Set up online/offline event listeners
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
    
    // Load cached data
    await this.loadCachedData();
    
    // Start periodic sync check
    this.startSyncCheck();
    
    console.log('✅ MobileOfflineManager: Offline support initialized');
  }

  async initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('📦 IndexedDB opened successfully');
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores
        if (!db.objectStoreNames.contains('userProfile')) {
          db.createObjectStore('userProfile', { keyPath: 'uid' });
        }
        
        if (!db.objectStoreNames.contains('gameStats')) {
          db.createObjectStore('gameStats', { keyPath: 'id' });
        }
        
        if (!db.objectStoreNames.contains('philosophyFlowSessions')) {
          const store = db.createObjectStore('philosophyFlowSessions', { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('analytics')) {
          const store = db.createObjectStore('analytics', { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('syncQueue')) {
          const store = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        console.log('🔧 IndexedDB schema updated');
      };
    });
  }

  async loadCachedData() {
    try {
      // Load user profile
      this.offlineData.userProfile = await this.getFromStore('userProfile', 'currentUser');
      
      // Load game stats
      this.offlineData.gameStats = await this.getAllFromStore('gameStats');
      
      // Load Philosophy Flow sessions
      this.offlineData.philosophyFlowSessions = await this.getAllFromStore('philosophyFlowSessions');
      
      // Load analytics
      this.offlineData.analytics = await this.getAllFromStore('analytics');
      
      console.log('📂 Cached data loaded:', {
        profile: !!this.offlineData.userProfile,
        gameStats: Object.keys(this.offlineData.gameStats).length,
        philosophySessions: this.offlineData.philosophyFlowSessions.length,
        analytics: this.offlineData.analytics.length
      });
    } catch (error) {
      console.error('❌ Failed to load cached data:', error);
    }
  }

  async saveToStore(storeName, data) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }
      
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getFromStore(storeName, key) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }
      
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFromStore(storeName) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }
      
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteFromStore(storeName, key) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }
      
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Public API methods
  async cacheUserProfile(profile) {
    try {
      await this.saveToStore('userProfile', { uid: 'currentUser', ...profile });
      this.offlineData.userProfile = profile;
      console.log('💾 User profile cached offline');
    } catch (error) {
      console.error('❌ Failed to cache user profile:', error);
    }
  }

  async cacheGameStats(gameType, stats) {
    try {
      await this.saveToStore('gameStats', { id: gameType, ...stats });
      this.offlineData.gameStats[gameType] = stats;
      console.log(`💾 ${gameType} stats cached offline`);
    } catch (error) {
      console.error(`❌ Failed to cache ${gameType} stats:`, error);
    }
  }

  async savePhilosophyFlowSession(sessionData) {
    try {
      const session = {
        ...sessionData,
        timestamp: Date.now(),
        synced: false
      };
      
      if (this.isOnline) {
        // Try to sync immediately if online
        session.synced = await this.syncPhilosophyFlowSession(session);
      }
      
      await this.saveToStore('philosophyFlowSessions', session);
      this.offlineData.philosophyFlowSessions.push(session);
      
      if (!session.synced) {
        this.addToSyncQueue('philosophyFlow', session);
      }
      
      console.log('💾 Philosophy Flow session saved:', session.synced ? 'synced' : 'offline');
    } catch (error) {
      console.error('❌ Failed to save Philosophy Flow session:', error);
    }
  }

  async trackAnalytics(eventType, data) {
    try {
      // Temporarily disable analytics tracking to prevent API errors
      console.log(`📊 Analytics event (${eventType}) - disabled in development`);
      return;
      
      const analyticsEvent = {
        type: eventType,
        data,
        timestamp: Date.now(),
        synced: false,
        deviceType: 'mobile',
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`
      };
      
      if (this.isOnline) {
        // Try to sync immediately if online
        analyticsEvent.synced = await this.syncAnalyticsEvent(analyticsEvent);
      }
      
      await this.saveToStore('analytics', analyticsEvent);
      this.offlineData.analytics.push(analyticsEvent);
      
      if (!analyticsEvent.synced) {
        this.addToSyncQueue('analytics', analyticsEvent);
      }
      
      console.log(`📊 Analytics tracked (${eventType}):`, analyticsEvent.synced ? 'synced' : 'offline');
    } catch (error) {
      console.error('❌ Failed to track analytics:', error);
    }
  }

  async addToSyncQueue(type, data) {
    try {
      const queueItem = {
        type,
        data,
        timestamp: Date.now(),
        retries: 0,
        maxRetries: 3
      };
      
      await this.saveToStore('syncQueue', queueItem);
      this.syncQueue.push(queueItem);
      console.log(`📤 Added to sync queue: ${type}`);
    } catch (error) {
      console.error('❌ Failed to add to sync queue:', error);
    }
  }

  async syncPhilosophyFlowSession(session) {
    try {
      // Use the existing game API endpoint for Philosophy Flow sessions
      const response = await fetch('/api/game/save-encrypted-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'philosophy_flow_session',
          data: session
        })
      });
      
      return response.ok;
    } catch (error) {
      console.error('❌ Failed to sync Philosophy Flow session:', error);
      return false;
    }
  }

  async syncAnalyticsEvent(event) {
    try {
      // Use the existing game API endpoint for analytics
      const response = await fetch('/api/game/save-encrypted-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'mobile_analytics',
          data: event
        })
      });
      
      return response.ok;
    } catch (error) {
      console.error('❌ Failed to sync analytics event:', error);
      return false;
    }
  }

  async handleOnline() {
    console.log('🌐 Device came online - starting sync...');
    this.isOnline = true;
    await this.syncPendingData();
  }

  handleOffline() {
    console.log('📴 Device went offline - enabling offline mode');
    this.isOnline = false;
  }

  async syncPendingData() {
    try {
      const pendingItems = await this.getAllFromStore('syncQueue');
      console.log(`🔄 Syncing ${pendingItems.length} pending items...`);
      
      for (const item of pendingItems) {
        let success = false;
        
        if (item.type === 'philosophyFlow') {
          success = await this.syncPhilosophyFlowSession(item.data);
        } else if (item.type === 'analytics') {
          success = await this.syncAnalyticsEvent(item.data);
        }
        
        if (success) {
          await this.deleteFromStore('syncQueue', item.id);
          console.log(`✅ Synced ${item.type} item`);
        } else {
          item.retries++;
          if (item.retries >= item.maxRetries) {
            await this.deleteFromStore('syncQueue', item.id);
            console.log(`❌ Failed to sync ${item.type} item after ${item.maxRetries} retries`);
          } else {
            await this.saveToStore('syncQueue', item);
          }
        }
      }
      
      console.log('✅ Sync completed');
    } catch (error) {
      console.error('❌ Sync failed:', error);
    }
  }

  startSyncCheck() {
    // Check for pending sync every 30 seconds when online
    setInterval(() => {
      if (this.isOnline && this.syncQueue.length > 0) {
        this.syncPendingData();
      }
    }, 30000);
  }

  // Getter methods for offline data
  getUserProfile() {
    return this.offlineData.userProfile;
  }

  getGameStats(gameType) {
    return this.offlineData.gameStats[gameType] || {};
  }

  getPhilosophyFlowSessions() {
    return this.offlineData.philosophyFlowSessions;
  }

  isOfflineMode() {
    return !this.isOnline;
  }

  getStorageInfo() {
    return {
      isOnline: this.isOnline,
      syncQueueLength: this.syncQueue.length,
      cachedProfiles: !!this.offlineData.userProfile,
      cachedGameStats: Object.keys(this.offlineData.gameStats).length,
      cachedSessions: this.offlineData.philosophyFlowSessions.length,
      cachedAnalytics: this.offlineData.analytics.length
    };
  }

  async clearOfflineData() {
    try {
      const stores = ['userProfile', 'gameStats', 'philosophyFlowSessions', 'analytics', 'syncQueue'];
      
      for (const storeName of stores) {
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        await new Promise((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
      
      // Reset in-memory data
      this.offlineData = {
        userProfile: null,
        gameStats: {},
        philosophyFlowSessions: [],
        analytics: [],
        preferences: {}
      };
      this.syncQueue = [];
      
      console.log('🗑️ Offline data cleared');
    } catch (error) {
      console.error('❌ Failed to clear offline data:', error);
    }
  }
}
